import { apiService } from './api';

export interface UtilityGroup {
  id: string;
  propertyId: string;
  name: string;
  utilityType: string;
  splitMethod: 'EQUAL' | 'CUSTOM';
  unitIds: string[];
  shares?: Array<{ unit_id: string; share_ratio: number }>;
  createdAt?: string;
  /** Set when the group is derived from a utilityConfig entry rather than a utility_meter_group */
  fromConfig?: boolean;
  /** Billing method from utilityConfig: 'LUMP_SUM_DISTRIBUTION' | 'RATE_PER_UNIT' */
  billingMethod?: string;
  /** Rate per unit when billingMethod is RATE_PER_UNIT */
  ratePerUnit?: number | null;
}

export interface UtilityBillAllocation {
  tenantId: string;
  tenantName: string;
  unitId: string;
  unitNumber: string;
  amount: number;
  paymentId: string;
  invoiceId: string;
}

class UtilityManagementService {
  async getPropertyGroups(propertyId: string): Promise<UtilityGroup[]> {
    const res = await apiService.get(`/utilities/properties/${propertyId}/groups`);
    return res.data?.groups || [];
  }

  async createGroup(payload: {
    propertyId: string;
    name: string;
    utilityType: string;
    unitIds: string[];
    splitMethod?: 'EQUAL' | 'CUSTOM';
    ratePerUnit?: number | null;
  }): Promise<UtilityGroup> {
    const res = await apiService.post(`/utilities/properties/${payload.propertyId}/groups`, {
      name: payload.name,
      utilityType: payload.utilityType,
      unitIds: payload.unitIds,
      splitMethod: payload.splitMethod || 'EQUAL',
      ratePerUnit: payload.ratePerUnit ?? null,
    });
    return res.data?.group;
  }

  async updateGroup(payload: {
    groupId: string;
    name?: string;
    utilityType?: string;
    unitIds?: string[];
    splitMethod?: 'EQUAL' | 'CUSTOM';
    ratePerUnit?: number | null;
  }): Promise<UtilityGroup> {
    const res = await apiService.patch(`/utilities/groups/${payload.groupId}`, {
      name: payload.name,
      utilityType: payload.utilityType,
      unitIds: payload.unitIds,
      splitMethod: payload.splitMethod,
      ratePerUnit: payload.ratePerUnit,
    });
    return res.data?.group;
  }

  async distributeBill(payload: {
    groupId: string;
    billingPeriod: string;
    totalAmount: number;
    notes?: string;
    dueDate?: string;
  }): Promise<{ billId: string; allocations: UtilityBillAllocation[] }> {
    const res = await apiService.post(`/utilities/groups/${payload.groupId}/bills/distribute`, payload);
    return res.data?.result;
  }

  async getGroupBills(groupId: string): Promise<any[]> {
    const res = await apiService.get(`/utilities/groups/${groupId}/bills`);
    return res.data?.bills || [];
  }
}

export const utilityManagementService = new UtilityManagementService();
